<template>
  <div>
    <v-card>
      <v-card-title>
        <div>
          <div class="headline">Standard Comm/Disc Factor Information</div>
        </div>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-flex xs12 class="mt-3">
          <message :messages="messages"></message>
        </v-flex>
        <v-container fluid grid-list-lg>
          <v-layout row wrap>
            <v-flex xs12>
              <v-subheader class="queryLabel">{{labels.productQuery}}</v-subheader>
              <div class="title">{{stdCommDiscFactForm.product}}</div>
            </v-flex>
            <v-flex xs6 v-if="newStdCommDiscFact">
              <v-layout row wrap>
                <v-flex xs7>
                  <cal-picker v-model="effDate" :label="labels.effDate" v-validate="'required'" :data-vv-as="labels.effDate" data-vv-name="effDate" :error-messages="errors.collect('effDate')" @input="doEffDate"></cal-picker>
                </v-flex>
                <v-flex xs5>
                  <time-picker v-model="effTime" :label="labels.effTime" v-validate="'required'" :data-vv-as="labels.effTime" data-vv-name="effTime" :error-messages="errors.collect('effTime')" @input="doEffDate"></time-picker>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex xs6 v-else>
              <v-subheader class="queryLabel">{{labels.effDateQuery}}</v-subheader>
              <div class="title">{{formatDateTime2(stdCommDiscFactForm.effDate)}}</div>
            </v-flex>
            <v-flex xs6>
              <v-layout row wrap>
                <v-flex xs7>
                  <cal-picker v-model="endDate" :label="labels.endDate" v-validate="endDateValidation" :min="this.effDate" :data-vv-as="labels.endDate" data-vv-name="endDate" :error-messages="errors.collect('endDate')" @input="doEndDate"></cal-picker>
                </v-flex>
                <v-flex xs5>
                  <time-picker v-model="endTime" :label="labels.endTime" :data-vv-as="labels.endTime" data-vv-name="endTime" :error-messages="errors.collect('endTime')" @input="doEndDate"></time-picker>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex xs12>
              <v-layout>
                <v-flex xs4>
                  <v-autocomplete :items="factor1to3List" v-model="stdCommDiscFactForm.fact1" :label="labels.fact1" item-text="label" item-value="value" :error-messages="errors.collect('fact1')" data-vv-name="fact1" :data-vv-as="labels.fact1" clearable></v-autocomplete>
                </v-flex>
                <v-flex xs4>
                  <v-autocomplete :items="factor1to3List" v-model="stdCommDiscFactForm.fact2" :label="labels.fact2" item-text="label" item-value="value" :error-messages="errors.collect('fact2')" data-vv-name="fact2" :data-vv-as="labels.fact2" clearable></v-autocomplete>
                </v-flex>
                <v-flex xs4>
                  <v-autocomplete :items="factor1to3List" v-model="stdCommDiscFactForm.fact3" :label="labels.fact3" item-text="label" item-value="value" :error-messages="errors.collect('fact3')" data-vv-name="fact3" :data-vv-as="labels.fact3" clearable></v-autocomplete>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex xs12>
              <v-layout>
                <v-flex xs4>
                  <v-autocomplete :items="factor4to6List" v-model="stdCommDiscFactForm.fact4" :label="labels.fact4" item-text="label" item-value="value" :error-messages="errors.collect('fact4')" data-vv-name="fact4" :data-vv-as="labels.fact4" clearable></v-autocomplete>
                </v-flex>
                <v-flex xs4>
                  <v-autocomplete :items="factor4to6List" v-model="stdCommDiscFactForm.fact5" :label="labels.fact5" item-text="label" item-value="value" :error-messages="errors.collect('fact5')" data-vv-name="fact5" :data-vv-as="labels.fact5" clearable></v-autocomplete>
                </v-flex>
                <v-flex xs4>
                  <v-autocomplete :items="factor4to6List" v-model="stdCommDiscFactForm.fact6" :label="labels.fact6" item-text="label" item-value="value" :error-messages="errors.collect('fact6')" data-vv-name="fact6" :data-vv-as="labels.fact6" clearable></v-autocomplete>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex xs12>
              <v-btn color="primary" type="submit" :loading="submitting" @click.prevent="doSave">
                {{newStdCommDiscFact? 'Add Standard Comm/Disc Factor' : 'Update Standard Comm/Disc Factor'}}
                <v-icon right>{{newStdCommDiscFact? 'mdi-sale' : 'mdi-content-save'}}</v-icon>
                </v-btn>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>
    </v-card>
    <std-comm-disc-rates v-if="!newStdCommDiscFact" :stdCommDiscRates="stdCommDiscRates" :stdCommDiscFacts="factorForm" @doRemove="doRemove"></std-comm-disc-rates>
  </div>
</template>

<script>
import MessageMixin from '@/mixins/message'
import { expandURI } from '@/utils/ajax'
import { extractMenu } from '@/utils/extract'
import StdCommDiscRates from './StdCommDiscRates'
import { PRODUCT_STDCOMMDISCFACT, PRODUCT_STDCOMMDISCFACT_TABMENU, PRODUCT_STDCOMMDISCFACTORLIST } from '@/store/types'
import { formatDisplayNumber } from '@/utils/number'
import toDate from 'date-fns/toDate'
import { formatDisplayTime, formatDisplayDateTime2 } from '@/utils/date'

export default {
  data() {
    return {
      labels: {
        productQuery: 'Product',
        effDate: 'Effective Date*',
        effTime: 'Time*',
        effDateQuery: 'Effective Date',
        endDate: 'End Date',
        endTime: 'Time',
        fact1: 'Factor 1',
        fact2: 'Factor 2',
        fact3: 'Factor 3',
        fact4: 'Factor 4',
        fact5: 'Factor 5',
        fact6: 'Factor 6'
      },
      stdCommDiscFactForm: {
        product: undefined,
        effDate: undefined,
        endDate: undefined,
        fact1: undefined,
        fact2: undefined,
        fact3: undefined,
        fact4: undefined,
        fact5: undefined,
        fact6: undefined,
        revision: undefined
      },
      menu: [],
      newStdCommDiscFact: false,
      submitting: false,
      stdCommDiscRates: [],
      effDate: undefined,
      effTime: undefined,
      endDate: undefined,
      endTime: undefined,
      factor1to3Loading: false,
      factor1to3List: [],
      factor4to6Loading: false,
      factor4to6List: []
    }
  },
  computed: {
    productAppID() {
      return this.$store.state.productManagement.product.appID
    },
    factorList() {
      return this.$store.state.productManagement.product.factorList
    },
    factorForm() {
      const result = {
        fact1: this.stdCommDiscFactForm.fact1,
        fact2: this.stdCommDiscFactForm.fact2,
        fact3: this.stdCommDiscFactForm.fact3,
        fact4: this.stdCommDiscFactForm.fact4,
        fact5: this.stdCommDiscFactForm.fact5,
        fact6: this.stdCommDiscFactForm.fact6
      }
      return result
    },
    endDateValidation() {
      return 'start_enddate:' + this.stdCommDiscFactForm.effDate
    }
  },
  methods: {
    async doInit() {
      if (!this.newStdCommDiscFact) {
        this.stdCommDiscFactForm.product = this.product
        this.stdCommDiscFactForm.effDate = this.effectiveDate
        // Review
        await this.reviewStandardCommDiscFact()
        this.queryStandardCommDiscRates()
      }
      await Promise.all([this.getFactor1to3List(), this.getFactor4to6List()])
    },
    doEffDate() {
      // Format Effective Date
      if (this.effDate && this.effTime && this.effTime.length === 5) {
        const hr = this.effTime.substring(0, 2)
        const min = this.effTime.substring(3, 5)
        this.effDate = toDate(this.effDate)
        this.effDate.setHours(hr)
        this.effDate.setMinutes(min)
        this.stdCommDiscFactForm.effDate = this.effDate
      }
    },
    doEndDate() {
      // Format Expiry Date
      if (this.endDate && this.endTime && this.endTime.length === 5) {
        const hr = this.endTime.substring(0, 2)
        const min = this.endTime.substring(3, 5)
        this.endDate = toDate(this.endDate)
        this.endDate.setHours(hr)
        this.endDate.setMinutes(min)
        this.stdCommDiscFactForm.endDate = this.endDate
      } else {
        this.stdCommDiscFactForm.endDate = undefined
      }
    },
    async reviewStandardCommDiscFact() {
      const apiURI = this.$store.state.productManagement.product.menu.stdCommDiscFactorGet
      if (apiURI) {
        try {
          const expand = {
            product: this.product,
            effDate: formatDisplayDateTime2(this.stdCommDiscFactForm.effDate)
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          const { data } = result.data
          if (data) {
            // Standard Comm/Disc Factor init
            this.stdCommDiscFactForm.product = data.stdCommDiscFact.product
            this.stdCommDiscFactForm.fact1 = data.stdCommDiscFact.fact1
            this.stdCommDiscFactForm.fact2 = data.stdCommDiscFact.fact2
            this.stdCommDiscFactForm.fact3 = data.stdCommDiscFact.fact3
            this.stdCommDiscFactForm.fact4 = data.stdCommDiscFact.fact4
            this.stdCommDiscFactForm.fact5 = data.stdCommDiscFact.fact5
            this.stdCommDiscFactForm.fact6 = data.stdCommDiscFact.fact6
            this.stdCommDiscFactForm.revision = data.stdCommDiscFact.revision
            this.stdCommDiscFactForm.effDate = data.stdCommDiscFact.effDate
            this.stdCommDiscFactForm.endDate = data.stdCommDiscFact.endDate
            if (this.stdCommDiscFactForm.effDate) {
              this.effDate = this.stdCommDiscFactForm.effDate
              this.effTime = formatDisplayTime(this.stdCommDiscFactForm.effDate)
            }
            if (this.stdCommDiscFactForm.endDate) {
              this.endDate = this.stdCommDiscFactForm.endDate
              this.endTime = formatDisplayTime(this.stdCommDiscFactForm.endDate)
            } else {
              this.endTime = '00:00'
            }

            const stdCommDiscFactors = {
              fact1: this.stdCommDiscFactForm.fact1,
              fact2: this.stdCommDiscFactForm.fact2,
              fact3: this.stdCommDiscFactForm.fact3,
              fact4: this.stdCommDiscFactForm.fact4,
              fact5: this.stdCommDiscFactForm.fact5,
              fact6: this.stdCommDiscFactForm.fact6
            }
            this.$store.commit(PRODUCT_STDCOMMDISCFACT, stdCommDiscFactors)
            const commitData = {
              stdCommDiscFactTab: 1,
              displayFactReviewTab: true,
              effDate: this.stdCommDiscFactForm.effDate
            }
            this.$store.commit(PRODUCT_STDCOMMDISCFACT_TABMENU, commitData)
            // Extract menu
            this.menu = extractMenu(result.data._links)
          }
        } catch (error) {
          throw error
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    doSave() {
      this.resetMessages()
      this.$validator.validateAll().then(result => {
        if (result) {
          if (this.newStdCommDiscFact) {
            this.doCreate()
          } else {
            this.doUpdate()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doUpdate() {
      const apiURI = this.menu.update
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.post(apiURI, this.stdCommDiscFactForm)
          const { data } = result
          if (data) {
            // Show message via notify
            this.notify(data)
            await this.reviewStandardCommDiscFact()
          }
          this.submitting = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.submitting = false
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    async doCreate() {
      const apiURI = this.$store.state.productManagement.product.menu.stdCommDiscFactorCreate
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.post(apiURI, this.stdCommDiscFactForm)
          const { data } = result

          if (data) {
            // Show message via notify
            this.notify(data)

            // Stay on page
            this.newStdCommDiscFact = false
            await this.reviewStandardCommDiscFact()
            this.queryStandardCommDiscRates()
          }
          this.submitting = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.submitting = false
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    async queryStandardCommDiscRates() {
      const apiURI = this.$store.state.productManagement.product.menu.stdCommDiscRates
      if (apiURI) {
        try {
          this.loading = true
          const expand = {
            product: this.stdCommDiscFactForm.product
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          if (result.data) {
            this.totalItems = result.data.totalElements
            const { data } = result.data
            if (data) {
              // Format the data here
              this.stdCommDiscRates = []
              data.map((stdCommDiscRate) => {
                const convertStdCommDiscRate = {
                  product: stdCommDiscRate.product,
                  displayEffDate: this.formatDateTime2(stdCommDiscRate.effDate),
                  effDate: stdCommDiscRate.effDate,
                  endDate: this.formatDateTime2(stdCommDiscRate.endDate),
                  commGN: stdCommDiscRate.commGN,
                  commPct: stdCommDiscRate.commPct,
                  discPct: stdCommDiscRate.discPct,
                  fact1: stdCommDiscRate.fact1,
                  fact2: stdCommDiscRate.fact2,
                  fact3: stdCommDiscRate.fact3,
                  fact4: this.formatDisplayNumber(stdCommDiscRate.fact4),
                  fact5: this.formatDisplayNumber(stdCommDiscRate.fact5),
                  fact6: this.formatDisplayNumber(stdCommDiscRate.fact6),
                  protComm: this.formatBoolean(stdCommDiscRate.protComm),
                  protDisc: this.formatBoolean(stdCommDiscRate.protDisc),
                  revision: stdCommDiscRate.revision
                }
                this.stdCommDiscRates.push(convertStdCommDiscRate)
              })
            } else {
              this.stdCommDiscRates = []
            }
          }
          this.loading = false
        } catch (err) {
          throw err
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    async doRemove(selectedStdCommDiscRate) {
      const apiURI = this.$store.state.productManagement.product.menu.stdCommDiscRateDelete
      if (apiURI) {
        this.loading = true
        const expand = {
          product: selectedStdCommDiscRate.product,
          effDate: selectedStdCommDiscRate.effDate,
          fact1: selectedStdCommDiscRate.fact1,
          fact2: selectedStdCommDiscRate.fact2,
          fact3: selectedStdCommDiscRate.fact3,
          fact4: selectedStdCommDiscRate.fact4,
          fact5: selectedStdCommDiscRate.fact5,
          fact6: selectedStdCommDiscRate.fact6
        }
        try {
          const result = await this.$http.post(apiURI, expand)
          this.notify(result.data)
          // Reinit
          this.queryStandardCommDiscRates()
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    formatDisplayNumber(xNum) {
      return formatDisplayNumber(xNum)
    },
    formatBoolean(val) {
      if (val === undefined) {
        return 'No'
      } else {
        if (val === true) {
          return 'Yes'
        } else {
          return 'No'
        }
      }
    },
    formatDateTime2(dateTime) {
      return formatDisplayDateTime2(dateTime)
    },
    async getFactor1to3List() {
      const apiURI = this.$store.state.productManagement.product.menu.factor1to3ListGet
      if (apiURI) {
        try {
          this.factor1to3Loading = true
          const expand = {
            appId: this.productAppID
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          const { data } = result.data
          if (data) {
            this.factor1to3List = data
          }
          const commitData = {
            factor1to3List: this.factor1to3List,
            factor4to6List: this.factor4to6List,
            appId: this.productAppID
          }
          this.$store.commit(PRODUCT_STDCOMMDISCFACTORLIST, commitData)
          this.factor1to3Loading = false
        } catch (err) {
          throw err
        }
      } else {
        this.errorMessage('Invalid API URL')
      }
    },
    async getFactor4to6List() {
      const apiURI = this.$store.state.productManagement.product.menu.factor4to6ListGet
      if (apiURI) {
        try {
          this.factor4to6Loading = true
          const expand = {
            appId: this.productAppID
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          const { data } = result.data
          if (data) {
            this.factor4to6List = data
          }
          const commitData = {
            factor1to3List: this.factor1to3List,
            factor4to6List: this.factor4to6List,
            appId: this.productAppID
          }
          this.$store.commit(PRODUCT_STDCOMMDISCFACTORLIST, commitData)
          this.factor4to6Loading = false
        } catch (err) {
          throw err
        }
      } else {
        this.errorMessage('Invalid API URL')
      }
    }
  },
  created() {
    if (this.effectiveDate === 'New') {
      this.newStdCommDiscFact = true
      this.stdCommDiscFactForm.product = this.product
      this.effTime = '00:00'
      this.endTime = '00:00'
    }
    this.doInit()
  },
  props: {
    product: {
      type: String,
      default: ''
    },
    effectiveDate: {
      type: String,
      default: ''
    }
  },
  mixins: [
    MessageMixin
  ],
  components: {
    StdCommDiscRates
  }
}
</script>

<style lang="scss">

</style>
